<script lang="ts">
  /**
   * The delete confirm modal (R3.2 §3): a trust-boundary check, not
   * decoration. Names `name` + `@tag`, one irreversible-loss line, a
   * `[Download export first]` anchor, and a confirm field — `[Delete
   * permanently]` stays `aria-disabled` (never `disabled`: a disabled control
   * is skipped by screen readers entirely) until the trimmed field equals the
   * tag exactly, case-sensitive.
   *
   * Mounting contract: `NotebookCreateModal`'s — a blur-modal sibling with the
   * surface behind `inert`, the heading taking focus on mount, `Esc` cancelling.
   * Authors nothing itself: `onConfirm` hands the decision to the container.
   */
  import Icon from './Icon.svelte';
  import Check from 'lucide/dist/esm/icons/check.mjs';
  import X from 'lucide/dist/esm/icons/x.mjs';
  import Download from 'lucide/dist/esm/icons/download.mjs';
  import { ANONYMOUS_OWNER_ID, type NotebookSummary } from '../storage/operations';

  interface Props {
    notebook: NotebookSummary;
    onConfirm: () => void;
    onCancel: () => void;
    /**
     * The owner this browser is acting as (R7) — the host's, because the export
     * anchor is a RELAY request and the auth gate binds the path's owner segment
     * to the login. Optional so the modal renders standalone, where the
     * anonymous owner is the only owner there can be.
     */
    ownerId?: string;
  }

  let { notebook, onConfirm, onCancel, ownerId = ANONYMOUS_OWNER_ID }: Props = $props();

  let headingEl = $state<HTMLHeadingElement | null>(null);
  $effect(() => {
    headingEl?.focus();
  });

  let confirmText = $state('');

  /** Trimmed, then exact — case-sensitive: ` Work ` is not the tag `work`. */
  const armed = $derived(confirmText.trim() === notebook.tag);

  function commit() {
    if (!armed) return;
    onConfirm();
  }

  function onkeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
  }
</script>

<!--
  The blur backdrop and the sheet are one element: the blur is decoration,
  `inert` on the SURFACE BEHIND is what controls (the takeover's rule).
-->
<div
  class="modal-backdrop"
  data-testid="delete-notebook-modal"
  role="dialog"
  aria-modal="true"
  aria-labelledby="delete-notebook-heading"
  tabindex="-1"
  {onkeydown}
>
  <div class="modal-sheet">
    <h1 id="delete-notebook-heading" bind:this={headingEl} tabindex="-1" data-testid="delete-notebook-heading">
      Delete {notebook.name}?
    </h1>

    <p>
      This permanently deletes <strong>{notebook.name}</strong>
      <span class="tag">@{notebook.tag}</span> on every device.
      This cannot be undone.
    </p>

    <!-- The last-chance safeguard, so it has to resolve wherever the app does:
         in account mode the gate refuses the anonymous owner's path with a 401,
         and a download-first link that 401s is no safeguard at all. -->
    <a
      class="action"
      data-testid="delete-export"
      href={`/api/owners/${ownerId}/export?notebook_id=${notebook.notebook_id}`}
      download={`${notebook.name}.af1-export.json`}
    >
      <Icon node={Download} size={13} />
      <span>[ Download export first ]</span>
    </a>

    <label class="field">
      <span class="field-label">Type {notebook.tag} to confirm</span>
      <input
        type="text"
        data-testid="delete-confirm-input"
        bind:value={confirmText}
        autocomplete="off"
        spellcheck="false"
      />
    </label>

    <div class="modal-actions">
      <button
        class="action"
        type="button"
        data-testid="delete-confirm"
        aria-disabled={armed ? 'false' : 'true'}
        onclick={commit}
      ><Icon node={Check} />[ Delete permanently ]</button>
      <button
        class="action"
        type="button"
        data-testid="delete-cancel"
        onclick={onCancel}
      ><Icon node={X} />[ Cancel ]</button>
    </div>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: color-mix(in srgb, var(--ink-muted) 20%, transparent);
    backdrop-filter: blur(2px);
    z-index: 30;
  }

  .modal-sheet {
    width: min(100%, 32em);
    background: var(--paper);
    box-shadow: var(--shadow-modal);
    padding: 1.5rem 2rem;
  }

  h1 {
    font-size: 1.25rem;
    font-weight: normal;
    outline: none;
  }

  .tag {
    font-family: var(--font-mono, monospace);
    color: var(--accent);
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 0.9rem;
  }

  .field-label {
    font-size: 12px;
    color: var(--ink-muted);
  }

  input {
    font: inherit;
    font-size: 16px;
    border: none;
    border-bottom: 1px solid color-mix(in srgb, var(--ink-muted) 70%, transparent);
    background: transparent;
    padding: 2px 0;
  }

  .modal-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.4rem;
  }
</style>
